import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import type { TimelineEvent } from '../actions/types';
import type { StatusCatalog } from '../../../services/statusCatalog';
import { humanizeManufacturerStatus } from '../../work-queue/util';
import { poVendorLabel } from '../../../services/orthopulseOrder';

// formatBody implementations for EVENT_TYPE_VISUAL_CONFIG, split out of
// event-config.tsx to keep that file under the size ceiling.

export function rejectionBody(event: TimelineEvent): ReactNode {
    const reason = (event.payload as { reason?: string } | undefined)?.reason;
    if (!reason) return null;
    return (
        <p className="text-xs text-gray-700 italic leading-relaxed whitespace-pre-line">
            "{reason}"
        </p>
    );
}

const RETAINER_OPTION_LABELS: Record<string, string> = {
    '1_set': '1 set upper/lower',
    '2_sets': '2 sets upper/lower',
    '3_sets': '3 sets upper/lower',
    '4_sets': '4 sets upper/lower',
    outside_odo: 'Retainers sourced outside ODO'
};

export function refinementGoalBody(event: TimelineEvent): ReactNode {
    const goal = (event.payload as { refinementGoal?: string } | undefined)?.refinementGoal;
    if (!goal) return null;
    return (
        <p className="text-xs text-gray-700 italic leading-relaxed whitespace-pre-wrap">"{goal}"</p>
    );
}

export function retainerBody(event: TimelineEvent): ReactNode {
    const option = (event.payload as { retainerOption?: string } | undefined)?.retainerOption;
    if (!option) return null;
    const label = RETAINER_OPTION_LABELS[option] ?? option;
    return <p className="text-xs text-gray-700 leading-relaxed">Retainers: {label}</p>;
}

/** Ret-3: what was actually ordered. */
export function retentionManufacturingBody(event: TimelineEvent): ReactNode {
    const payload = event.payload as { retainerOption?: string; order?: string } | undefined;
    const option = payload?.retainerOption;
    if (!option) return null;
    const label = RETAINER_OPTION_LABELS[option] ?? payload?.order ?? option;
    return <p className="text-xs text-gray-700 leading-relaxed">Retainers: {label}</p>;
}

export function retentionRefusalBody(event: TimelineEvent): ReactNode {
    const outcome =
        event.type === 'refuse-retention-end' ? 'Treatment ended' : 'Continuing treatment';
    const reason = (event.payload as { reason?: string } | undefined)?.reason;
    return (
        <div className="text-xs text-gray-700 leading-relaxed space-y-1">
            <p>
                <strong className="text-gray-900">{outcome}</strong>
            </p>
            {reason && <p className="italic">"{reason}"</p>}
        </div>
    );
}

export function recordsResubmissionBody(event: TimelineEvent): ReactNode {
    const count = (event.payload as { fileCount?: number } | undefined)?.fileCount ?? 0;
    if (count === 0) {
        return <p className="text-xs text-gray-500 italic leading-relaxed">No files uploaded.</p>;
    }
    return (
        <p className="text-xs text-gray-700 leading-relaxed">
            {count} {count === 1 ? 'file' : 'files'} uploaded
        </p>
    );
}

export function manufacturerSubmissionBody(event: TimelineEvent): ReactNode {
    const p = event.payload as
        { manufacturerCaseId?: string; manufacturerLink?: string } | undefined;
    if (!p?.manufacturerCaseId && !p?.manufacturerLink) return null;
    return (
        <div className="flex items-center gap-1.5 flex-wrap">
            {p.manufacturerCaseId && (
                <span className="inline-flex items-center gap-1 px-2 py-[3px] rounded-md bg-gray-100 text-gray-700 text-[11px] font-mono">
                    <span className="text-gray-400">#</span>
                    {p.manufacturerCaseId}
                </span>
            )}
            {p.manufacturerLink && (
                <a
                    href={p.manufacturerLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 px-2 py-[3px] rounded-md border border-gray-300 bg-white text-[11px] font-medium text-gray-600 transition-colors hover:border-gray-400 hover:text-gray-900"
                >
                    <ArrowTopRightOnSquareIcon className="w-3 h-3 text-gray-400" />
                    Open
                </a>
            )}
        </div>
    );
}

export function manufacturerStatusBody(event: TimelineEvent): ReactNode {
    // Keyed fromStatus/toStatus (not from/to) so this automation event is
    // excluded from the workflow journey / entry-time transition replays.
    const p = event.payload as { fromStatus?: string | null; toStatus?: string } | undefined;
    if (!p?.toStatus) return null;
    // Reuse the shared manufacturer-status humanizer so labels match the pills
    // and filters elsewhere (e.g. IN_PRODUCTION -> "In production").
    const to = humanizeManufacturerStatus(p.toStatus);
    return (
        <div className="text-xs text-gray-700 leading-relaxed">
            {p.fromStatus ? (
                <p>
                    <strong className="text-gray-900">
                        {humanizeManufacturerStatus(p.fromStatus)}
                    </strong>
                    {' → '}
                    <strong className="text-gray-900">{to}</strong>
                </p>
            ) : (
                <p>
                    Set to <strong className="text-gray-900">{to}</strong>
                </p>
            )}
        </div>
    );
}

export function designPlanBody(event: TimelineEvent): ReactNode {
    const plan = (event.payload as { designPlan?: string } | undefined)?.designPlan;
    if (!plan) return null;
    return (
        <p className="text-xs text-gray-700 italic leading-relaxed whitespace-pre-wrap">"{plan}"</p>
    );
}

export function clarificationProvidedBody(event: TimelineEvent): ReactNode {
    const response = (event.payload as { response?: string } | undefined)?.response;
    if (!response) return null;
    return (
        <p className="text-xs text-gray-700 italic leading-relaxed whitespace-pre-wrap">
            "{response}"
        </p>
    );
}

export function forceStatusBody(event: TimelineEvent, catalog: StatusCatalog): ReactNode {
    const p = event.payload as { from?: string; to?: string; reason?: string } | undefined;
    if (!p?.to && !p?.reason) return null;
    return (
        <div className="text-xs text-gray-700 leading-relaxed space-y-1">
            {p.to && (
                <p>
                    {p.from && (
                        <>
                            from{' '}
                            <strong className="text-gray-900">
                                {catalog.display(p.from)}
                            </strong>{' '}
                        </>
                    )}
                    to <strong className="text-gray-900">{catalog.display(p.to)}</strong>
                </p>
            )}
            {p.reason && <p className="italic">"{p.reason}"</p>}
        </div>
    );
}

const HOLD_REASON_LABELS: Record<string, string> = {
    'records-incomplete': 'Records incomplete',
    'in-office-step': 'In-office step needed',
    'refinement-needed': 'Refinement needed',
    'retention-needed': 'Retention needed',
    'clinical-question': 'Clinical question'
};

export function enterHoldBody(event: TimelineEvent): ReactNode {
    const p = event.payload as { reason?: string; note?: string } | undefined;
    if (!p?.reason && !p?.note) return null;
    const reasonLabel = p.reason ? (HOLD_REASON_LABELS[p.reason] ?? p.reason) : null;
    return (
        <div className="text-xs text-gray-700 leading-relaxed space-y-1">
            {reasonLabel && <p>{reasonLabel}</p>}
            {p.note && <p className="italic">"{p.note}"</p>}
        </div>
    );
}

export function setPriorityBody(event: TimelineEvent): ReactNode {
    const p = event.payload as { from?: string; to?: string; reason?: string } | undefined;
    if (!p?.from && !p?.to) return null;
    return (
        <div className="text-xs text-gray-700 leading-relaxed space-y-1">
            {p.from && p.to && (
                <p>
                    <strong className="text-gray-900 capitalize">{p.from}</strong>
                    {' → '}
                    <strong className="text-gray-900 capitalize">{p.to}</strong>
                </p>
            )}
            {p.reason && <p className="italic">"{p.reason}"</p>}
        </div>
    );
}

interface AdminFieldChange {
    key: string;
    label: string;
    from: unknown;
    to: unknown;
}

function formatChangeValue(v: unknown): string {
    if (v === null || v === undefined || v === '') return '-';
    return String(v);
}

export function adminPatientUpdateBody(event: TimelineEvent): ReactNode {
    const changes = (event.payload as { changes?: AdminFieldChange[] } | undefined)?.changes;
    if (!changes || changes.length === 0) return null;
    return (
        <div className="text-xs text-gray-700 leading-relaxed space-y-1">
            {changes.map((c) => (
                <p key={c.key}>
                    <span className="text-gray-500">{c.label}:</span>{' '}
                    <strong className="text-gray-900">{formatChangeValue(c.from)}</strong>
                    {' → '}
                    <strong className="text-gray-900">{formatChangeValue(c.to)}</strong>
                </p>
            ))}
        </div>
    );
}

interface MovePracticePayload {
    practiceIdBefore?: string | null;
    practiceIdAfter?: string;
    practiceNameBefore?: string | null;
    practiceNameAfter?: string;
    practiceDoctorBefore?: string | null;
    practiceDoctorAfter?: string | null;
}

// "Name (Dr. X)" like the picker rows; doctor omitted when the practice has none on file.
function practiceLabel(name: string | null | undefined, doctor: string | null | undefined): string {
    if (!name) return 'no practice';
    return doctor ? `${name} (${doctor})` : name;
}

export function movePracticeBody(event: TimelineEvent): ReactNode {
    const p = event.payload as MovePracticePayload | undefined;
    if (!p?.practiceIdAfter) return null;
    // practiceNameAfter is only absent on events written before the names fix.
    const hasNames = Boolean(p.practiceNameAfter);
    const from = hasNames
        ? practiceLabel(p.practiceNameBefore, p.practiceDoctorBefore)
        : (p.practiceIdBefore ?? 'no practice');
    const to = hasNames
        ? practiceLabel(p.practiceNameAfter, p.practiceDoctorAfter)
        : p.practiceIdAfter;
    return (
        <p className="text-xs text-gray-700 leading-relaxed">
            Moved from <strong className="text-gray-900">{from}</strong> to{' '}
            <strong className="text-gray-900">{to}</strong>
        </p>
    );
}

interface OrthopulseOrderedPayload {
    poNumber?: string;
    // A products.vendor slug, so any value the vendor catalogue holds.
    vendor?: string;
    // Absent on events written before the market field existed.
    market?: string;
    recipients?: string[];
}

export function orthopulseOrderedBody(event: TimelineEvent): ReactNode {
    const p = event.payload as OrthopulseOrderedPayload | undefined;
    if (!p?.poNumber) return null;
    const vendorLabel = p.vendor ? poVendorLabel(p.vendor, p.market) : null;
    const recipients = p.recipients?.join(', ') ?? '';
    return (
        <p className="text-xs text-gray-700 leading-relaxed">
            PO <span className="font-mono">{p.poNumber}</span>
            {vendorLabel && <> · {vendorLabel}</>}
            {recipients && <> · sent to {recipients}</>}
        </p>
    );
}
